import React, { useState } from "react";

import { faSparkles } from "@fortawesome/pro-regular-svg-icons";
import { noop } from "lodash-es";

import {
  BUILT_IN_TOOLS,
  isBuiltInTool,
  useAgentModels,
} from "src/api/agents/management";
import { ToolCard } from "src/base-components/agents/ToolCard";
import { Checkbox } from "src/base-components/Checkbox";
import { FormItem } from "src/base-components/FormItem";
import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import { Pill } from "src/base-components/Pill";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { Switch } from "src/base-components/Switch";
import { Tabs } from "src/base-components/Tabs";
import { Textarea } from "src/base-components/Textarea";
import type {
  AgentModelInfo,
  AgentVersion,
  ToolType,
} from "src/clients/agent-management";
import { Accordion } from "src/design-system/Accordion";
import { ModelIcon } from "src/genai/ModelSelector/ModelIcon";
import { WhitePane } from "src/jobs/common/WhitePane";
import type { JsonSchema } from "src/jsonSchema/types";
import { JSONSchemaRenderer } from "src/jsonSchema/viewer/JSONSchemaRenderer";

type Props = {
  version: AgentVersion;
  agentName: string;
  agentDescription: string;
};

export const AgentVersionReadOnlyView: React.FC<Props> = ({
  version,
  agentName,
  agentDescription,
}) => {
  const { configuration } = version;
  const tools = configuration.tools ?? [];
  const builtInTools = tools.filter((t) => isBuiltInTool(t.type));
  const configurableTools = tools.filter((t) => !isBuiltInTool(t.type));

  const { data: models } = useAgentModels();
  const modelInfo = models?.find(
    (m) => m.model_id === configuration.model_config.model,
  );

  const inputSchema = configuration.input_schema;
  const outputSchema = configuration.output_schema;
  const hasInputSchema =
    inputSchema &&
    inputSchema.properties &&
    Object.keys(inputSchema.properties).length > 0;
  const hasOutputSchema =
    outputSchema &&
    outputSchema.properties &&
    Object.keys(outputSchema.properties).length > 0;

  const customizationCategories = configuration.customizations_display ?? [];
  const hasCustomizations = customizationCategories.length > 0;

  return (
    <div className="h-auto">
      <WhitePane noMinWidth>
        <div className="w-full">
          {/* Agent Header */}
          <div className="border-gray-200 border-b pt-5 pr-10 pb-6 pl-11">
            <h2 className="font-inter-semibold-16px text-gray-800">
              {agentName}
            </h2>
            <div className="mt-2">
              <Pill size="sm" variant="indigo-light">
                <Pill.Icon icon={faSparkles} />
                <Pill.Text>Taktile Agents</Pill.Text>
              </Pill>
            </div>
          </div>

          {/* Content */}
          <div className="pt-9 pr-10 pb-11 pl-11">
            <OverviewSection
              agentDescription={agentDescription}
              modelDisplayName={
                modelInfo?.display_name ?? configuration.model_config.model
              }
              modelInfo={modelInfo}
            />

            <Accordion dataLoc="prebuilt-sections" defaultValue={["tools"]}>
              <Accordion.Item
                dataLoc="tools-accordion"
                label="Tools"
                value="tools"
              >
                <ToolsSection
                  builtInTools={builtInTools}
                  configurableTools={configurableTools}
                />
              </Accordion.Item>

              {(hasInputSchema || hasOutputSchema) && (
                <Accordion.Item
                  dataLoc="schema-accordion"
                  forceMount
                  label="Schema"
                  value="schema"
                >
                  <SchemaSection
                    hasInputSchema={!!hasInputSchema}
                    hasOutputSchema={!!hasOutputSchema}
                    inputSchema={inputSchema}
                    outputSchema={outputSchema}
                  />
                </Accordion.Item>
              )}

              {hasCustomizations && (
                <Accordion.Item
                  dataLoc="customizations-accordion"
                  label="Customizations"
                  value="customizations"
                >
                  <CustomizationsSection categories={customizationCategories} />
                </Accordion.Item>
              )}
            </Accordion>
          </div>
        </div>
      </WhitePane>
    </div>
  );
};

const OverviewSection: React.FC<{
  modelDisplayName: string;
  modelInfo: AgentModelInfo | undefined;
  agentDescription: string;
}> = ({ modelDisplayName, modelInfo, agentDescription }) => (
  <div className="mb-8">
    <div className="mb-5 flex items-center justify-between">
      <h3 className="font-inter-semibold-14px text-gray-800">Overview</h3>
      <div className="flex items-center gap-x-1.5 font-inter-normal-12px text-gray-800">
        {modelInfo && (
          <ModelIcon
            modelName={modelDisplayName}
            provider={modelInfo.provider}
          />
        )}
        <span>{modelDisplayName}</span>
      </div>
    </div>
    {agentDescription && (
      <div className="rounded-lg bg-gray-50 p-4">
        <MarkdownRenderer
          content={agentDescription}
          dataLoc="prebuilt-agent-description"
        />
      </div>
    )}
  </div>
);

const ToolsSection: React.FC<{
  builtInTools: NonNullable<AgentVersion["configuration"]["tools"]>;
  configurableTools: NonNullable<AgentVersion["configuration"]["tools"]>;
}> = ({ builtInTools, configurableTools }) => (
  <div className="rounded-lg bg-gray-50 p-4">
    <div className="flex flex-col gap-3">
      {builtInTools.map((tool) => {
        // Built-in tools don't persist name/description, so resolve them
        // from the registry. Membership is guaranteed by the isBuiltInTool
        // filter that produced builtInTools.
        const builtIn = BUILT_IN_TOOLS.find((t) => t.type === tool.type);
        return (
          <ToolCard
            key={tool.id}
            dataLoc={`readonly-tool-card-${tool.id}`}
            description={builtIn?.description ?? tool.description}
            name={builtIn?.name ?? tool.name}
            type={tool.type as ToolType}
          />
        );
      })}
      {configurableTools.map((tool) => (
        <ToolCard
          key={tool.id}
          dataLoc={`readonly-tool-card-${tool.id}`}
          description={tool.description}
          name={tool.name}
          type={tool.type as ToolType}
        />
      ))}
      {configurableTools.length === 0 && builtInTools.length === 0 && (
        <span className="font-inter-normal-13px text-gray-500">
          No tools configured.
        </span>
      )}
    </div>
  </div>
);

const SCHEMA_DISPLAY_OPTIONS = {
  showTypeLabels: false,
  showPrimitiveTypeLabels: true,
  showRequired: true,
  showDataPaths: false,
  showPrimitiveDataPath: false,
  autoExpandDepth: 1,
  canCollapse: false,
} as const;

const SchemaSection: React.FC<{
  inputSchema: { [key: string]: unknown } | null | undefined;
  outputSchema: { [key: string]: unknown } | null | undefined;
  hasInputSchema: boolean;
  hasOutputSchema: boolean;
}> = ({ inputSchema, outputSchema, hasInputSchema, hasOutputSchema }) => {
  const [activeTab, setActiveTab] = useState<string>(
    hasInputSchema ? "input" : "output",
  );

  const tabs = [
    {
      key: "input",
      label: "Input",
      content: hasInputSchema ? (
        <JSONSchemaRenderer
          displayOptions={SCHEMA_DISPLAY_OPTIONS}
          schema={inputSchema as unknown as JsonSchema}
        />
      ) : (
        <p className="py-3 font-inter-normal-13px text-gray-500 italic">
          No input schema defined
        </p>
      ),
    },
    {
      key: "output",
      label: "Output",
      content: hasOutputSchema ? (
        <JSONSchemaRenderer
          displayOptions={SCHEMA_DISPLAY_OPTIONS}
          schema={outputSchema as unknown as JsonSchema}
        />
      ) : (
        <p className="py-3 font-inter-normal-13px text-gray-500 italic">
          No output schema defined
        </p>
      ),
    },
  ];

  return (
    <div className="rounded-lg bg-gray-50 px-5 py-3">
      <Tabs
        panelsClassName="pt-8 pb-2"
        selectedKey={activeTab}
        tabListClassName="border-b border-gray-200"
        tabs={tabs}
        onChange={setActiveTab}
      />
    </div>
  );
};

type CustomizationCategories = NonNullable<
  AgentVersion["configuration"]["customizations_display"]
>;
type CustomizationField = NonNullable<
  CustomizationCategories[number]["fields"]
>[number];

const CustomizationsSection: React.FC<{
  categories: CustomizationCategories;
}> = ({ categories }) => (
  <div className="rounded-lg bg-white">
    <Accordion
      dataLoc="customization-categories"
      defaultValue={categories.length > 0 ? [categories[0].name] : []}
    >
      {categories.map((category) => (
        <Accordion.Item
          key={category.name}
          dataLoc={`customization-category-${category.name}`}
          description={category.description ?? undefined}
          label={category.name}
          value={category.name}
        >
          <div className="flex flex-col gap-y-8">
            {(category.fields ?? []).map((field) => (
              <CustomizationFieldView key={field.name} field={field} />
            ))}
          </div>
        </Accordion.Item>
      ))}
    </Accordion>
  </div>
);

const CustomizationFieldView: React.FC<{ field: CustomizationField }> = ({
  field,
}) => {
  switch (field.type) {
    case "text":
      return (
        <FormItem
          className="max-w-lg"
          description={field.description ?? undefined}
          gap={false}
          label={field.name}
        >
          <Textarea autoGrow disabled fullWidth readOnly value={field.value} />
        </FormItem>
      );
    case "boolean":
      return (
        <div className="flex max-w-lg items-start gap-x-20">
          <div>
            <span className="font-inter-semibold-13px text-gray-800">
              {field.name}
            </span>
            {field.description && (
              <p className="mt-1 font-inter-normal-12px text-gray-500">
                {field.description}
              </p>
            )}
          </div>
          <Switch disabled enabled={field.value} onChange={noop} />
        </div>
      );
    case "enum":
      return (
        <FormItem
          description={field.description ?? undefined}
          gap={false}
          label={field.name}
        >
          <SimpleRadioGroup
            disabled
            orientation="horizontal"
            value={field.value}
            onValueChange={noop}
          >
            <div className="grid w-full grid-cols-3 gap-x-4 gap-y-2">
              {field.options.map((option) => (
                <SimpleRadioGroup.Item
                  key={option}
                  label={option}
                  labelClassName="ml-2"
                  value={option}
                />
              ))}
            </div>
          </SimpleRadioGroup>
        </FormItem>
      );
    case "multi_select":
      return (
        <FormItem
          description={field.description ?? undefined}
          gap={false}
          label={field.name}
        >
          <div className="grid w-full grid-cols-3 gap-x-4 gap-y-2">
            {field.options.map((option) => (
              <label
                key={option}
                className="flex items-center gap-x-2 font-inter-normal-12px text-gray-700 [&>input]:cursor-not-allowed"
              >
                <Checkbox checked={field.value.includes(option)} readOnly />
                {option}
              </label>
            ))}
          </div>
        </FormItem>
      );
    default:
      return null;
  }
};
